/** Tolerant recursive-descent parser: query string -> FilterNode[].
 *
 * Never throws. A user can hand-edit a URL's `?q=`, so anything the parser
 * can't cleanly interpret is dropped and reported via `diagnostics` rather than
 * producing a broken node (a structurally-invalid node would break toWireAst).
 *
 * Grammar:
 *   orExpr    := andExpr ('|' andExpr)*      -- 2+ terms -> {group, op:'OR'}
 *   andExpr   := element+                    -- adjacency IS AND; '&' is a
 *                                               separator (flushes the freetext
 *                                               buffer, produces no node itself)
 *   element   := '!' negatable | negatable | wordRun
 *   negatable := '(' orExpr ')' | condition
 *   condition := NSOP value
 *   value     := scalar ('..' scalar)?
 *
 * Freetext buffering is implemented inside the AND loop (not as a grammar
 * rule): bare WORD/QUOTED tokens accumulate into a buffer and flush as ONE
 * `{kind:'freetext'}` node at the next boundary (&, |, (, ), !, NSOP, or EOF).
 */
import { sigilOperator } from './operators';
import { tokenize, type Sigil, type Token } from './tokenizer';
import type { Diagnostic, FilterNode, FilterValue, NamespaceConfigMap, QueryOperator } from './types';

export function parse(
  text: string,
  namespaces: NamespaceConfigMap,
): { nodes: FilterNode[]; diagnostics: Diagnostic[] } {
  const { tokens, diagnostics } = tokenize(text);
  const parser = new Parser(tokens, namespaces, diagnostics);
  const nodes = parser.parseTop();
  return { nodes, diagnostics };
}

class Parser {
  private pos = 0;

  constructor(
    private readonly tokens: Token[],
    private readonly namespaces: NamespaceConfigMap,
    private readonly diagnostics: Diagnostic[],
  ) {}

  private peek(): Token | undefined {
    return this.tokens[this.pos];
  }

  private next(): Token | undefined {
    return this.tokens[this.pos++];
  }

  private diag(pos: number, message: string): void {
    this.diagnostics.push({ pos, message });
  }

  /** Parse the whole stream and normalise the top-level shape. */
  parseTop(): FilterNode[] {
    const node = this.parseOr();
    if (node === null) return [];
    if (node.kind === 'group' && node.op === 'AND' && !node.not) {
      // A top-level AND collapses to its children (wire top-level is implicit
      // AND — a nested AND group at the root would be redundant).
      return node.children;
    }
    // Single non-group node, or a top-level OR group: keep as a 1-element array
    // (OR must NOT be unwrapped, so its semantics survive round-tripping).
    return [node];
  }

  /** orExpr := andExpr ('|' andExpr)*  — returns null if nothing parsed. */
  private parseOr(): FilterNode | null {
    const terms: FilterNode[] = [];
    const first = this.parseAnd();
    if (first !== null) terms.push(first);

    while (this.peek()?.kind === 'PIPE') {
      const pipe = this.next()!; // consume '|'
      const term = this.parseAnd();
      if (term !== null) {
        terms.push(term);
      } else if (this.peek() === undefined) {
        // Dangling trailing '|'.
        this.diag(pipe.pos, 'Trailing "|" operator dropped.');
      }
    }

    if (terms.length === 0) return null;
    if (terms.length === 1) return terms[0]!;
    return { kind: 'group', op: 'OR', children: terms };
  }

  /** andExpr := element+  (adjacency = AND; explicit '&' is a separator). */
  private parseAnd(): FilterNode | null {
    const children: FilterNode[] = [];
    let buffer: string[] = [];

    const flush = () => {
      if (buffer.length === 0) return;
      children.push({ kind: 'freetext', value: buffer.join(' ') });
      buffer = [];
    };

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const t = this.peek();
      if (t === undefined) break;

      // Boundaries that terminate this AND expression entirely.
      if (t.kind === 'PIPE' || t.kind === 'RPAREN') {
        break;
      }

      if (t.kind === 'AMP') {
        // Explicit '&' is just a separator: flush any pending freetext and
        // continue (produces no node of its own).
        flush();
        this.next();
        continue;
      }

      if (t.kind === 'WORD' || t.kind === 'QUOTED') {
        buffer.push(t.text);
        this.next();
        continue;
      }

      if (t.kind === 'BANG') {
        flush();
        this.next(); // consume '!'
        const node = this.parseNegatable(t.pos);
        if (node !== null) children.push(node);
        continue;
      }

      if (t.kind === 'LPAREN') {
        flush();
        const node = this.parseGroup();
        if (node !== null) children.push(node);
        continue;
      }

      if (t.kind === 'NSOP') {
        flush();
        const node = this.parseCondition();
        if (node !== null) children.push(node);
        continue;
      }

      // A stray RANGE at this position is meaningless — skip it.
      if (t.kind === 'RANGE') {
        this.diag(t.pos, 'Unexpected "..".');
        this.next();
        continue;
      }

      // Should be unreachable, but stay tolerant.
      this.next();
    }

    flush();

    if (children.length === 0) return null;
    if (children.length === 1) {
      // A lone element at the top level or inside a group is that element.
      return children[0]!;
    }
    return { kind: 'group', op: 'AND', children };
  }

  /** After a '!' has been consumed: negate a group or a condition. */
  private parseNegatable(bangPos: number): FilterNode | null {
    const t = this.peek();
    if (t === undefined) {
      this.diag(bangPos, 'Trailing "!" operator dropped.');
      return null;
    }
    if (t.kind === 'LPAREN') {
      const group = this.parseGroup();
      return group === null ? null : applyNot(group);
    }
    if (t.kind === 'NSOP') {
      const cond = this.parseCondition();
      return cond === null ? null : applyNot(cond);
    }
    if (t.kind === 'WORD' || t.kind === 'QUOTED') {
      // `!my free` negates the ENTIRE coalesced freetext run as one node, not
      // just the first word: adjacent-word coalescing wins over "! binds one
      // token". This is deliberate and safe because the serializer always emits
      // explicit &/| between siblings (never bare adjacency), so this choice
      // never introduces round-trip ambiguity.
      const texts: string[] = [];
      let cur = this.peek();
      while (cur !== undefined && (cur.kind === 'WORD' || cur.kind === 'QUOTED')) {
        texts.push(cur.text);
        this.next();
        cur = this.peek();
      }
      return { kind: 'freetext', value: texts.join(' '), not: true };
    }
    // '!' followed by something un-negatable (e.g. '|') — drop the '!'.
    this.diag(bangPos, 'Dangling "!" operator dropped.');
    return null;
  }

  /** '(' orExpr ')' — with bracket-mismatch tolerance. */
  private parseGroup(): FilterNode | null {
    const lparen = this.next()!; // consume '('
    const inner = this.parseOr();
    const closing = this.peek();
    if (closing?.kind === 'RPAREN') {
      this.next(); // consume ')'
    } else {
      this.diag(lparen.pos, 'Unclosed "(" — implicitly closed at end of input.');
    }
    if (inner === null) return null;
    // A parenthesised single node collapses to that node (a size-1 group is
    // never canonical). This is what makes `!(departure=08:00)` a negated cond,
    // not a negated 1-child group.
    return inner;
  }

  /** condition := NSOP value  — returns null (dropped) on any error. */
  private parseCondition(): FilterNode | null {
    const nsop = this.next()!; // NSOP
    if (nsop.kind !== 'NSOP') return null;
    const ns = nsop.ns;
    const cfg = this.namespaces[ns];
    if (!cfg) {
      this.diag(nsop.pos, `Unknown namespace "${ns}".`);
      // Consume the value tokens so they don't leak into freetext.
      this.skipValue();
      return null;
    }

    const first = this.peek();
    // Open-start range `ns=..v` → the `lt` operator (namespaces that declare it).
    if (first?.kind === 'RANGE') {
      this.next(); // consume '..'
      const bound = this.peek();
      if (bound === undefined || (bound.kind !== 'WORD' && bound.kind !== 'QUOTED')) {
        this.diag(nsop.pos, `Missing value for "${ns}".`);
        return null;
      }
      this.next();
      if (!cfg.operators.includes('lt')) {
        this.diag(nsop.pos, `Namespace "${ns}" does not support open-ended ranges (..v).`);
        return null;
      }
      return { kind: 'cond', namespace: ns, operator: 'lt', value: bound.text, ...(nsop.all ? { scope: 'all' } : {}) };
    }
    if (first === undefined || (first.kind !== 'WORD' && first.kind !== 'QUOTED')) {
      this.diag(nsop.pos, `Missing value for "${ns}".`);
      return null;
    }
    const firstVal = first.text;
    this.next();

    // Range? A `..` following the first scalar.
    if (this.peek()?.kind === 'RANGE') {
      this.next(); // consume '..'
      const second = this.peek();
      if (second === undefined || (second.kind !== 'WORD' && second.kind !== 'QUOTED')) {
        // Open-end range `ns=v..` → the `gt` operator (namespaces that declare it).
        if (cfg.operators.includes('gt')) {
          return { kind: 'cond', namespace: ns, operator: 'gt', value: firstVal, ...(nsop.all ? { scope: 'all' } : {}) };
        }
        this.diag(nsop.pos, `Range for "${ns}" is missing its upper bound.`);
        return null;
      }
      this.next();
      if (!cfg.operators.includes('range')) {
        this.diag(nsop.pos, `Namespace "${ns}" does not support ranges.`);
        return null;
      }
      const value: FilterValue = [firstVal, second.text];
      return { kind: 'cond', namespace: ns, operator: 'range', value, ...(nsop.all ? { scope: 'all' } : {}) };
    }

    const operator = this.resolveScalarOperator(ns, nsop.sigil, cfg.operators, nsop.pos);
    if (operator === null) return null;
    return { kind: 'cond', namespace: ns, operator, value: firstVal, ...(nsop.all ? { scope: 'all' } : {}) };
  }

  /** Consume the tokens that would have formed a value (for dropped conds). */
  private skipValue(): void {
    const t = this.peek();
    // Open-start range value: `..v`.
    if (t?.kind === 'RANGE') {
      this.next();
      const bound = this.peek();
      if (bound && (bound.kind === 'WORD' || bound.kind === 'QUOTED')) this.next();
      return;
    }
    if (t === undefined || (t.kind !== 'WORD' && t.kind !== 'QUOTED')) return;
    this.next();
    if (this.peek()?.kind === 'RANGE') {
      this.next();
      const second = this.peek();
      if (second && (second.kind === 'WORD' || second.kind === 'QUOTED')) this.next();
    }
  }

  /** Resolve a scalar operator from the sigil (shared rule — operators.ts),
   *  turning a null resolution into a diagnostic. */
  private resolveScalarOperator(
    ns: string,
    sigil: Sigil,
    declared: QueryOperator[],
    pos: number,
  ): string | null {
    const operator = sigilOperator(sigil, declared);
    if (operator !== null) return operator;
    const detail =
      sigil === '=~'
        ? `does not support "contains" (=~)`
        : sigil === '=#'
          ? `does not support match-by-id (=#)`
          : `does not support an equality match`;
    this.diag(pos, `Namespace "${ns}" ${detail}.`);
    return null;
  }
}

/** Apply `not` to a node. For a group/cond/freetext this toggles onto the node
 *  itself; a double negation cancels (keeps the tree canonical). */
function applyNot(node: FilterNode): FilterNode {
  if (node.not) {
    const rest = { ...node };
    delete rest.not;
    return rest;
  }
  return { ...node, not: true };
}
